import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { checkEmail, signIn, signUp } from '../../Module/Auth/Presentation/authActions';
import { useAppDispatch } from '../../store/hooks';
import { PATHS } from '../../routes/paths';

export type FundraiserType = 'individual' | 'group' | 'foreigner';
type EmailCheckStatus = 'idle' | 'checking' | 'available' | 'duplicate' | 'error';
const SUPPRESS_POST_SIGNUP_MODALS_KEY = 'missionfund.suppressPostSignupModals';

export const useSignup = () => {
  const { t } = useTranslation('translation');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const snsInfo = location.state as { sns_id?: string; sns_phone?: string; sns_thumbnail?: string } | undefined;

  const [step, setStep] = useState(0); // 0: Type Selection, 1: Email, 2: Password, 3: Additional Info, 4: Welcome
  const [fundraiserType, setFundraiserType] = useState<FundraiserType>('individual');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [country, setCountry] = useState('');
  const [category, setCategory] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [emailCheckStatus, setEmailCheckStatus] = useState<EmailCheckStatus>('idle');
  const [emailCheckMessage, setEmailCheckMessage] = useState<string | undefined>(undefined);
  const latestEmailCheckId = useRef(0);

  const trimmedEmail = useMemo(() => email.trim(), [email]);
  const isEmailFormatValid = useCallback((value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value), []);
  const isEmailAvailable = emailCheckStatus === 'available';

  useEffect(() => {
    if (!trimmedEmail) {
      setEmailCheckStatus('idle');
      setEmailCheckMessage(undefined);
      return;
    }

    if (!isEmailFormatValid(trimmedEmail)) {
      setEmailCheckStatus('error');
      setEmailCheckMessage('유효한 이메일 주소를 입력해 주세요.');
      return;
    }

    const currentCheckId = ++latestEmailCheckId.current;
    const timer = window.setTimeout(async () => {
      setEmailCheckStatus('checking');
      setEmailCheckMessage('이메일 중복 확인 중...');

      try {
        const result = await dispatch(checkEmail({ email: trimmedEmail })).unwrap();
        if (latestEmailCheckId.current !== currentCheckId) return;

        if (result.available) {
          setEmailCheckStatus('available');
          setEmailCheckMessage('사용 가능한 이메일입니다.');
          return;
        }

        setEmailCheckStatus('duplicate');
        setEmailCheckMessage('이미 사용 중인 이메일입니다.');
      } catch (e) {
        if (latestEmailCheckId.current !== currentCheckId) return;
        const msg = typeof e === 'string' ? e : (e as Error)?.message;
        setEmailCheckStatus('error');
        setEmailCheckMessage(msg || '이메일 확인 중 오류가 발생했습니다.');
      }
    }, 500);

    return () => {
      window.clearTimeout(timer);
    };
  }, [dispatch, isEmailFormatValid, t, trimmedEmail]);

  const passwordMessage = useMemo(() => {
    if (password && passwordConfirm && password !== passwordConfirm) {
      return '새 비밀번호와 재입력한 비밀번호가 일치하지 않습니다.';
    }
    return undefined;
  }, [password, passwordConfirm]);

  const handleNext = useCallback(() => {
    setStep((prev) => {
      if (prev === 1 && !isEmailAvailable) return prev;
      return prev + 1;
    });
  }, [isEmailAvailable]);

  const handlePrev = useCallback(() => {
    setStep((prev) => Math.max(0, prev - 1));
  }, []);

  const handleConfirm = useCallback(async () => {
    if (isSubmitting) return;
    if (!isEmailAvailable) {
      setError(emailCheckMessage || t('page.signup.errors.default'));
      return;
    }

    try {
      setIsSubmitting(true);
      setError(undefined);

      const partner = (process.env.REACT_APP_PARTNER as string | undefined) ?? 'OMF';
      const trimmedName = name.trim();
      const isIndividual = fundraiserType === 'individual';
      const nameParts = trimmedName.split(/\s+/).filter(Boolean);
      const first_name = nameParts[0] ?? trimmedName;
      const last_name = nameParts.slice(1).join(' ') || nameParts[0] || trimmedName;

      const res = await dispatch(
        signUp({
          first_name,
          last_name,
          email: email.trim(),
          partner,
          user_passwd: password,
          phone: (isIndividual && snsInfo?.sns_phone) ? snsInfo.sns_phone : phoneNumber.trim(),
          // 회원 유형은 상세 코드(Missionary/NGO 등)로 저장
          user_type: category,
          user_desc: category,
          country_code: country,
          // 단체 회원: 입력한 단체명을 organization 필드에도 전달
          ...(isIndividual ? {} : { organization: trimmedName }),
          sns_id: snsInfo?.sns_id,
          sns_phone: snsInfo?.sns_phone,
          sns_thumbnail: snsInfo?.sns_thumbnail,
        }),
      ).unwrap();

      const ok = res?.result_message?.acknowledged ?? true;
      if (!ok) throw new Error(t('page.signup.errors.default'));

      try {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(SUPPRESS_POST_SIGNUP_MODALS_KEY, '1');
        }
        await dispatch(signIn({ user_email: email.trim(), password, partner })).unwrap();
      } catch {
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(SUPPRESS_POST_SIGNUP_MODALS_KEY);
        }
      }

      setStep(4);
    } catch (e) {
      const msg = typeof e === 'string' ? e : (e as Error)?.message;
      setError(msg || t('page.signup.errors.default'));
    } finally {
      setIsSubmitting(false);
    }
  }, [category, country, dispatch, email, emailCheckMessage, fundraiserType, isEmailAvailable, isSubmitting, name, password, phoneNumber, t]);

  const goToLogin = useCallback(() => {
    const userId = window.localStorage.getItem('missionfund.userId');
    navigate(userId ? PATHS.DASHBOARD : PATHS.LOGIN);
  }, [navigate]);

  return {
    step,
    setStep,
    fundraiserType,
    setFundraiserType,
    email,
    setEmail,
    password,
    setPassword,
    passwordConfirm,
    setPasswordConfirm,
    name,
    setName,
    phoneNumber,
    setPhoneNumber,
    country,
    setCountry,
    category,
    setCategory,
    handleNext,
    handlePrev,
    handleConfirm,
    isSubmitting,
    error,
    emailCheckStatus,
    emailCheckMessage,
    isEmailAvailable,
    passwordMessage,
    goToLogin,
  };
};

